import { LevelMonitorList } from 'app/agriculture/routes/Devices/LevelMonitorList/LevelMonitorList';
import {
    Route,
    createBrowserRouter,
    createRoutesFromChildren,
    createRoutesFromElements,
    matchRoutes,
    useNavigationType,
    useLocation,
} from 'react-router-dom';
import { NotFound } from 'app/agriculture/routes/NotFound';
import { RootErrorElement } from 'app/agriculture/FarmMain/RootErrorElement';
import { TerrainOverlayLegend } from 'app/agriculture/legend/terrain_overlay_legend';
import { routeConfig } from 'app/agriculture/router/routeConfig';
import { renderRouteComponentWithSuspense } from 'app/agriculture/router/renderRouteComponentWithSuspense';
import { HerdMove } from 'app/agriculture/routes/Herds/HerdMove/HerdMove';
import { WeatherModal } from 'app/agriculture/routes/WeatherModal/WeatherModal';
import { DeviceMoreDetails } from 'app/agriculture/routes/Devices/DeviceMoreDetails/DeviceMoreDetails';
import { TankLink } from 'app/agriculture/routes/Tanks/TankLink/TankLink';
import { TankUnlink } from 'app/agriculture/routes/Tanks/TankLink/TankUnlink';
import { FarmSwitchDialog } from 'app/agriculture/FarmMain/TopBar/FarmSwitchDialog';
import { LevelMonitorListSkeleton } from 'app/agriculture/routes/Devices/LevelMonitorList/components/LevelMonitorListSkeleton';
import { DeviceDetailsSkeleton } from 'app/agriculture/routes/Devices/DeviceDetails/components/DeviceDetailsSkeleton';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { AppOutlet } from 'app/agriculture/AppOutlet';
import { FarmMain } from 'app/agriculture/FarmMain/FarmMain';
import { backOfficeRoutes } from 'app/agriculture/routes/BackOffice/backOfficeRoutes';
import { WeatherStationList } from 'app/agriculture/routes/Devices/WeatherStationList/WeatherStationList';
import { WeatherStationListSkeleton } from 'app/agriculture/routes/Devices/WeatherStationList/components/WeatherStationListSkeleton';
import { SetDefaultMapView } from 'app/agriculture/routes/SetDefaultMapView/SetDefaultMapView';
import { ConfigService } from 'app/config/config.service';

export const routes = createRoutesFromElements(
    <Route path="/" element={<AppOutlet />} errorElement={<RootErrorElement />}>
        {backOfficeRoutes}

        <Route path=":farmId" element={<FarmMain />}>
            <Route index element={<></>} />

            {Object.values(routeConfig).map(
                ({ rootPath, routeElements: { List, Create, Details, Edit, Delete } }) => (
                    <Route path={rootPath} key={rootPath} errorElement={<NotFound />}>
                        <Route index element={renderRouteComponentWithSuspense(List)} />
                        <Route path="create" element={renderRouteComponentWithSuspense(Create)} />
                        <Route path=":id">
                            <Route index element={renderRouteComponentWithSuspense(Details)} />
                            <Route path="edit" element={renderRouteComponentWithSuspense(Edit)} />
                            <Route
                                path="delete"
                                element={renderRouteComponentWithSuspense(Delete)}
                            />
                        </Route>
                    </Route>
                ),
            )}

            {/* Special cased routes - we will fix this with CU-86cwujrck */}
            <Route
                path="herds/:id/move"
                element={renderRouteComponentWithSuspense({ Component: HerdMove })}
            />
            <Route
                path=":entityRootPath/:id/link"
                element={renderRouteComponentWithSuspense({ Component: TankLink })}
            />
            <Route
                path=":entityRootPath/:id/unlink"
                element={renderRouteComponentWithSuspense({ Component: TankUnlink })}
            />
            <Route
                path="devices/:id/more-details"
                element={renderRouteComponentWithSuspense({
                    Component: DeviceMoreDetails,
                    Skeleton: DeviceDetailsSkeleton,
                })}
            />
            <Route
                path="weather-dashboard"
                element={renderRouteComponentWithSuspense({ Component: WeatherModal })}
            />
            <Route path="switch-farm" element={<FarmSwitchDialog />} />

            <Route
                path="terrain-overlay-legend/:terrainType"
                element={renderRouteComponentWithSuspense({ Component: TerrainOverlayLegend })}
            />

            <Route
                path="devices/level-monitors/"
                element={renderRouteComponentWithSuspense({
                    Component: LevelMonitorList,
                    Skeleton: LevelMonitorListSkeleton,
                })}
            />

            <Route
                path="devices/weather-stations/"
                element={renderRouteComponentWithSuspense({
                    Component: WeatherStationList,
                    Skeleton: WeatherStationListSkeleton,
                })}
            />

            <Route
                path="default-view/"
                element={renderRouteComponentWithSuspense({
                    Component: SetDefaultMapView,
                })}
            />

            <Route path="*" element={<NotFound />} />
        </Route>
    </Route>,
);

if (ConfigService.sentryDsn != null) {
    Sentry.init({
        dsn: ConfigService.sentryDsn,
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.captureConsoleIntegration({ levels: ['warn', 'error', 'debug', 'assert'] }),
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: false,
                blockAllMedia: false,
                networkDetailAllowUrls: [window.location.origin],
            }),
            Sentry.replayCanvasIntegration(),
        ],
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    });
}

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
export const router = sentryCreateBrowserRouter(routes);
